"use client";

import { useTransitionRouter } from "next-view-transitions";
import { useEffect } from "react";
import {
  installStartViewTransitionShim,
  type NavDirection,
} from "src/lib/start-view-transition-shim";
import { startEdgeSwipeListener } from "src/lib/use-edge-swipe-back";
import useViewTransitionsEnabled from "src/lib/use-view-transitions-enabled";

/**
 * One-shot direction override for the NEXT `document.startViewTransition`
 * call. Set it RIGHT BEFORE calling startViewTransition (or before the
 * code path that will call it, e.g. `dispatchWithTransition`) and the
 * patched wrapper will use it instead of defaulting to "forward".
 *
 * Used by step-machine pop transitions where there is no popstate event
 * to imply the back direction — e.g. the home wizard dismissing the
 * paywall or stepping back from checkout. Forward-direction calls don't
 * need this; "forward" is the default for any non-popstate SVT.
 */
let nextDirectionOverride: NavDirection | undefined = undefined;

export function setOwletNextDirection(direction: NavDirection): void {
  nextDirectionOverride = direction;
}

/**
 * Owlet-only view-transition glue. Three responsibilities:
 *
 * 1. Pick the slide direction by wrapping `document.startViewTransition`.
 *    Setting `data-nav-direction` only on click/popstate left a stale
 *    value on programmatic `router.push` calls (e.g. button-driven nav
 *    inside the home wizard), so a back nav followed by a forward push
 *    would replay the back slide. The wrapper resets the attribute on
 *    every startViewTransition call: "back" if a popstate just fired
 *    (history pop), otherwise "forward".
 *
 *    The popstate-just-fired flag is CONSUMED by the patched
 *    startViewTransition (the lib's popstate handler calls it
 *    synchronously inside the same event dispatch). We do not clear
 *    via queueMicrotask: WebKit on iOS has been observed draining
 *    microtasks between event listeners in places the spec doesn't
 *    strictly require, which made the flag go stale before the lib's
 *    bubble-phase popstate handler read it. A defensive 100 ms timeout
 *    clears the flag if (somehow) no SVT call follows the popstate.
 *
 * 2. Intercept same-origin <a> clicks in the capture phase and route
 *    them through `useTransitionRouter` so the navigation is wrapped in
 *    document.startViewTransition. next/link by itself does NOT wrap
 *    navigations in Next 16.1.x (the canary docs describe a feature
 *    that isn't shipped). Without this, clicks on visit cards, "Past
 *    visits", etc. produce no transition.
 *
 * 3. Default <html>'s `data-nav-direction` to "forward" on mount so the
 *    very first navigation has a sensible value before any transition
 *    has fired.
 *
 * Mount once at the root for Owlet (see layout.tsx). No-op when the
 * browser does not support document.startViewTransition.
 */
export default function OwletNavDirectionTracker() {
  const supported = useViewTransitionsEnabled();
  const router = useTransitionRouter();

  useEffect(() => {
    if (!supported) return;

    const html = document.documentElement;
    html.dataset.navDirection = "forward";

    // popstate-just-fired flag. Set to true by the popstate listener,
    // consumed (set back to false) by the wrapped startViewTransition.
    let popstateActive = false;
    let popstateTimeout: ReturnType<typeof setTimeout> | null = null;

    const onPopState = () => {
      // Belt: write "back" directly here, synchronously. This is what
      // the original implementation did and it definitively works.
      // Even if patched SVT (suspenders) fails to read popstateActive
      // as true for any reason, the direction is already correct.
      html.dataset.navDirection = "back";
      popstateActive = true;
      if (popstateTimeout) clearTimeout(popstateTimeout);
      popstateTimeout = setTimeout(() => {
        popstateActive = false;
        popstateTimeout = null;
      }, 100);
    };

    // Suspenders for the popstate belt above: see start-view-transition-shim.ts
    // for why we patch document.startViewTransition. The shim consumes the
    // popstate flag and direction override that the tracker owns.
    const restoreSVT = installStartViewTransitionShim({
      html,
      consumePopstate: () => {
        if (!popstateActive) return false;
        popstateActive = false;
        if (popstateTimeout) {
          clearTimeout(popstateTimeout);
          popstateTimeout = null;
        }
        return true;
      },
      consumeOverride: () => {
        const v = nextDirectionOverride;
        nextDirectionOverride = undefined;
        return v;
      },
    });

    const onClick = (event: MouseEvent) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const link = target?.closest?.("a");
      if (!link) return;

      if (link.hasAttribute("download")) return;
      const linkTarget = link.getAttribute("target");
      if (linkTarget && linkTarget !== "_self") return;

      const href = link.getAttribute("href");
      if (!href) return;
      if (
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("javascript:")
      ) {
        return;
      }

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;

      event.preventDefault();
      event.stopPropagation();

      router.push(url.pathname + url.search + url.hash);
    };

    // popstate goes on window (its real target). No capture flag — the
    // DOM dispatch on target=window invokes listeners in insertion
    // order, and React effect order (children first) guarantees our
    // listener registers before next-view-transitions' listener inside
    // the parent ViewTransitions provider.
    window.addEventListener("popstate", onPopState);
    document.addEventListener("click", onClick, true);

    // Start the opt-in edge-swipe-back listener. Pages with a back or
    // close button register a handler via useEdgeSwipeBack; the
    // gesture is inert on screens that don't.
    const stopEdgeSwipe = startEdgeSwipeListener();

    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", onClick, true);
      if (popstateTimeout) clearTimeout(popstateTimeout);
      restoreSVT();
      stopEdgeSwipe();
    };
  }, [supported, router]);

  return null;
}
